import type { AutofixInpaintCheckoutRequest, AutofixOutpaintCheckoutRequest, AutofixRecolorCheckoutRequest, AutoFixRequest } from '@/types/autoFixRequest'
import type { GenerateRequest } from '@/types/generateRequest'
import type { ValidateRequest } from '@/types/validateRequest'
import type { Validation, ValidationResult } from '@/types/validation'
import type { CreativeResponse } from '@/types/creativeResponse'
import type { EnhanceRequest } from '@/types/enhanceRequest'
import type { WriteLabelsAndBriefsRequest, WriteUsingAIRequest } from '@/types/writeUsingAIRequest'
import type { HasBackgroundImageRequest } from '@/types/HasBackgroundImageRequest'
import { useAppStore } from '@/stores/app'
import { getAuthHeaders } from '@/helpers/apiHelper'
import AiApiClient from '@/utils/aiApiClient'
import type { GenerateBgWithOverlayRequest } from '@/types/generateBgWithOverlayRequest'
import type { DetectionsResult, OutpaintingResult, RecoloringResult, InpaintingResult, OutpaintRequest } from '@/types/AutoFixResponse'
import type { GenerateAIShadowsRequest } from '@/types/aiShadows'

class CreativeService {
  private readonly aiApiService
  private readonly aiBackgroundService

  constructor() {
    this.aiApiService = new AiApiClient(import.meta.env.VITE_SAM_API_URL)
    this.aiBackgroundService = new AiApiClient(import.meta.env.VITE_APP_BACKGROUND_IMAGE_URL)
  }

  async handleDetections(requestData: AutoFixRequest, signal: AbortSignal): Promise<DetectionsResult> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/auto-generate-with-canvas-json`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: signal
      },
      false,
      1,
      true
    )
    if (response.status === 204) {
      return {
        status: 204,
        message: "No products present. Autofix not possible."
      }
    }
    if (response.status === 500) {
      return {
        status: 500,
        message: response.message
      }
    }
    return {
      status: response.status,
      data: response
    };
  }

  async handleInpaint(requestData: AutofixInpaintCheckoutRequest, signal: AbortSignal): Promise<InpaintingResult> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/auto-generate-from-inpaint`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: signal
      },
      false,
      1,
      true
    )
    if (response.status === 500) {
      return {
        status: 500,
        message: response.message
      }
    }
    return {
      status: response.status,
      data: response
    };
  }

  async handleOutpaint(requestData: OutpaintRequest, signal: AbortSignal): Promise<OutpaintingResult> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/outpaint-image`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: signal
      },
      false,
      1,
      true
    )
    if (response.status === 500) {
      return {
        status: 500,
        message: response.message
      }
    }
    return {
      status: response.status,
      data: response
    };
  }

  async handleRecolor(requestData: AutofixRecolorCheckoutRequest, signal: AbortSignal): Promise<RecoloringResult> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/auto-generate-recolor`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestData),
        signal: signal
      },
      false,
      1,
      true
    )
    return {
      status: response.status,
      data: response
    };
  }

  async generateCreative(requestData: GenerateRequest): Promise<CreativeResponse> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/generate-with-canvas-json`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestData),
      },
    )

    return response
  }

  async validateCreative(requestData: ValidateRequest): Promise<ValidationResult> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/validate-with-adcheck`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestData),
      },
      false,
      1,
      true
    )
    return {
      status: response.status,
      data: response
    }
  }

  async enhanceCreative(requestData: EnhanceRequest): Promise<any> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/enhance-image`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      },
    )

    return response
  }

  async addEnhancedBackground(requestData: object): Promise<any> {
    const appStore = useAppStore()
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${appStore.selectedBusinessProfileId}/generate-effect`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      },
    )

    return response
  }

  async writeUsingAI(businessProfileId: number, requestData: WriteUsingAIRequest): Promise<any> {
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/write-headlines-using-ai`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(requestData),
      },
    )

    return response
  }

  async writeLabelsAndBriefs(
    businessProfileId: number,
    requestData: WriteLabelsAndBriefsRequest,
  ): Promise<any> {
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/get-labels-and-briefs`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      },
    )

    return response.labels
  }

  async generateBackgroundTexture(
    businessProfileId: number,
    prompt: string,
    brandName: string,
    aspectRatio: string,
    width: number,
    height: number,
    numberOfTextures: number,
  ): Promise<any> {
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/generate-background-texture-image`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ prompt, brandName, aspectRatio, width, height, numberOfTextures }),
      },
    )

    return response
  }

  async GenerateBackgroundWithOverlay(
    businessProfileId: number | null,
    request: GenerateBgWithOverlayRequest,
  ): Promise<string> {
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/generate-background-with-overlay`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      },
    )

    return response
  }

  async generateAIShadows(
    businessProfileId: number,
    requestData: GenerateAIShadowsRequest,
  ): Promise<any> {
    const response = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/generate-ai-shadows`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      },
    )

    return response
  }

  async hasBackground(
    businessProfileId: number | null,
    sourceUrl: string,
    transparencyRatioThreshold = 0.1,
    alphaThreshold = 200,
  ): Promise<boolean> {
    const requestData: HasBackgroundImageRequest = {
      imageUrl: sourceUrl,
      alphaThreshold,
      transparencyRatioThreshold,
    }
    const responseData = await this.aiApiService.fetchWithRetry(
      `bp/${businessProfileId}/has-background`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData),
      },
    )
    // returning boolean value either True or False
    return responseData
  }
}

export default CreativeService
